import React from "react";
import { useTranslationStore } from "../../../state/useTranslationStore";
import AddLanguageButton from "./AddLanguageButton";
import { toast } from "sonner";

const GregorianNumbersEditor: React.FC = () => {
  const {
    gregorian_numbers,
    activeLanguage,
    availableLanguages,
    loading,
    selectLanguage,
    updateTranslation,
    saveTranslations,
    addNewLanguage,
  } = useTranslationStore();

  // Filter Gregorian numbers for the active language and track original index
  const activeGregorianNumbers = gregorian_numbers
    .map((number, idx) => ({ ...number, originalIndex: idx }))
    .filter((number) => number.language_code === activeLanguage);

  // Handle input changes using the correct index in original array
  const handleInputChange = (originalIndex: number, value: string) => {
    if (originalIndex >= 0 && originalIndex < gregorian_numbers.length) {
      updateTranslation("gregorian_numbers", originalIndex, value);
    } else {
      console.error("Invalid index for gregorian_numbers");
    }
  };

  // Handle saving translations
  const handleSave = async () => {
    try {
      await saveTranslations();
      toast.success("Gregorian numbers updated successfully!");
    } catch (error) {
      console.error("Error saving Gregorian numbers:", error);
      toast.error("Failed to save Gregorian numbers.");
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-indigo-600">Edit Gregorian Numbers</h1>

      {/* Language Selection */}
      <div className="mb-4 flex gap-4 flex-wrap">
        <div className="space-x-3">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => selectLanguage(lang.code)}
              className={`px-3 py-2 rounded-md focus:outline-none focus:ring-2 capitalize ${
                activeLanguage === lang.code
                  ? "bg-indigo-500 text-white"
                  : "bg-gray-200"
              }`}
            >
              {lang.language}
            </button>
          ))}
        </div>
        <AddLanguageButton
          data={gregorian_numbers}
          handlerAddLangauge={addNewLanguage}
          disabled={loading}
        />
      </div>

      {/* Translation Inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {activeGregorianNumbers.map((number) => (
          <div
            key={`${number.original_text}-${number.language_code}`}
            className="flex flex-col gap-2"
          >
            <label className="text-gray-700 font-medium">
              {number.original_text} ({number.language_code})
            </label>
            <input
              type="text"
              value={number.translated_text}
              onChange={(e) =>
                handleInputChange(number.originalIndex, e.target.value)
              }
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={loading}
            />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default GregorianNumbersEditor;
